/**
 * @providesModule LeagueInfoUtils
 */
import {
  League,
  LeagueCarouselItem,
  LeaguePlayer,
  LeaguePrizeConfiguration,
  LeaguePrizeValue,
  LeaguePrizeType,
  LeagueType,
  LeaguePrizeRange,
  LeaguePrizeUnique,
  LeaguePrizeDefault,
} from '@api/Leagues';
import {getLeagues, queryKeys as queryKeysLeagues} from '@api/GetLeagues';
import {GetLeaguePlayersResult, getLeaguePlayers} from '@api/GetLeaguePlayers';
import {
  getLeagueSets,
  GetLeagueSetsResult,
  queryKeys as queryKeysLeagueSet,
} from '@api/GetLeagueSets';
import {
  ELeagueMedal,
  ICurrencyViewProps,
  ILeagueInfoMacroCellProps,
  ILeagueInfoMacroScreenProps,
  ILeagueInfoScreenProps,
  ILeagueLeaderboardCarouselCellProps,
  ILeagueLeaderboardCarouselProps,
  ILeagueLeaderboardCellProps,
  ILeagueLockedScreenProps,
  ILeaguePodiumItemProps,
  ILeaguePodiumViewProps,
  ILeaguePrizeInfoScreenProps,
  ILeagueResultPrizePageProps,
  ILeagueWelcomeScreenProps,
  ILeagueWinEarnProps,
  IMedalBreakdown,
  IProgressiveLeagueLeaderboardPlayerCell,
} from '@skillz/ui';
import {OptingButtonType} from '@skillz/ui/lib/typescript/src/components/leagues/ProgressiveLeagueHeader/LeagueHeaderTypes';
import {
  ECurrencyCode,
  TCurrencyCode,
  abbreviatedMedal,
  getInfoFromCurrencyCode,
} from '@skillz/ui/src/utils/prizes';
import {LeagueThemeSchema, ThemeType} from '@skillz-rn/utils';
import {getTierImage} from 'AvatarView';
import AssetHelper from 'AssetHelper';
import ClientPropertyConstants from 'ClientPropertyConstants';
import I18n, {_m} from 'INTL';
import _ from 'lodash';
import {
  PAID_LEAGUE_OPT_IN_MODAL_CURRENT_LEAGUE_ID_LAUNCH,
  PAID_LEAGUE_OPT_IN_MODAL_CURRENT_LEAGUE_ID_LEAGUES_TAB,
  PAID_LEAGUE_OPT_IN_MODAL_LAST_PROMPT_TIMESTAMP_LAUNCH,
  PAID_LEAGUE_OPT_IN_MODAL_LAST_PROMPT_TIMESTAMP_LEAGUES_TAB,
  PAID_LEAGUE_OPT_IN_MODAL_PROMPT_COUNT_SEASON_LAUNCH,
  PAID_LEAGUE_OPT_IN_MODAL_PROMPT_COUNT_SEASON_LEAGUES_TAB,
  PaidLeagueOptInModalFlow,
} from 'ProgressiveLeaguesConstants';
import Prefs from 'Preferences';
import SKZConstants from 'SKZConstants';
import SkillzStyle from 'SkillzStyle';
import {ImageSourcePropType, Platform} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {QueryClient} from '@tanstack/react-query';

/**
 * Types the information we use to determine if user moved tier placement by comparing current place with previous place from local storage.
 * We only save the necessary data (current_player) from the /players api response to avoid flooding local storage.
 */
export type SavedLeagueCurrentPlayerData = Pick<GetLeaguePlayersResult, 'current_player'> | null;

export interface LeagueInterstitialResult {
  /** The league saved in the local storage for reference.*/
  savedLeagueRef: League | null;
  /** Current League assigned to the user */
  currentLeagueRef: League | null;
  /** Bool indicating whether the "instant promotion" interstitial should be shown. */
  showLeagueInstantPromotionInterstitial: boolean;
  /** Bool indicating whether the league status interstitial should be shown. */
  showLeagueStatusInterstitial?: boolean;
  /** Players data saved in local storage for use on league status interstitial. Will be compared to currentLeaguePlayerData */
  savedLeaguePlayerData?: SavedLeagueCurrentPlayerData | null;
  /** Players data in the user's current league.*/
  currentLeaguePlayerData?: GetLeaguePlayersResult | null;
  /** League set for the game */
  currentLeagueSetData?: GetLeagueSetsResult | null;
}

const getEarnIcon = (league: League) => {
  return league.total_prize_value.map((leaguePrize) => {
    switch (leaguePrize.type) {
      case LeaguePrizeType.CASH:
      case LeaguePrizeType.BONUS_CASH:
        return {
          label: _m('Cash Currency'),
          image: getInfoFromCurrencyCode({
            currencyCode: getCurrencyCode(leaguePrize.currency),
          }).getIcon({
            themeType: getLeagueTheme(league).mode,
            isBonus: leaguePrize.type === LeaguePrizeType.BONUS_CASH,
          })!,
        };
      case LeaguePrizeType.TICKETZ:
        return {
          label: _m('Ticketz'),
          image: getInfoFromCurrencyCode({
            currencyCode: ECurrencyCode.TICKETZ,
          }).getIcon({
            themeType: getLeagueTheme(league).mode,
          })!,
        };
      case LeaguePrizeType.VIRTUAL_CURRENCY:
        return {
          label: _m('Practice Currency'),
          image: getInfoFromCurrencyCode({
            currencyCode: ECurrencyCode.VIRTUAL,
            theme: SkillzStyle.themeJSON,
          }).getIcon({
            themeType: getLeagueTheme(league).mode,
          })!,
        };
      default:
        return {label: _m('Special Prizes'), image: {uri: 'ic_welcome_back_prize_skz.png'}};
    }
  });
};

export const getLeagueMode = (ltm?: 'light' | 'dark' | null): ThemeType | undefined => {
  if (!ltm) {
    return undefined;
  }
  if (ltm === 'light') {
    return 'Light';
  } else {
    return 'Dark';
  }
};

export const getLeagueTheme = (league: League): Partial<LeagueThemeSchema> => {
  const leagueMode = getLeagueMode(league.additional_fields?.league_theme_mode);
  const primaryColor = league.primary_color || undefined;
  const secondaryColor = league.secondary_color || undefined;

  return {
    mode: leagueMode,
    primary_color: primaryColor,
    secondary_color: secondaryColor,
  };
};

const getWinEarnProps = (league: League): ILeagueWinEarnProps => {
  return {
    win: {
      label: league.medal.name,
      image: {uri: league.medal.url},
      medalType: getMedalType(league.type),
    },
    earn: getEarnIcon(league),
  };
};

/**
 * Get the league information props
 * @param league The league from the API
 * @param callbacks The callbacks on the league info page UI
 * @returns the ILeagueInfoScreenProps
 */
export const getLeagueInfoProps = (
  league: League,
  callbacks: Pick<ILeagueInfoScreenProps, 'onPlayPress' | 'onClosePress'>
): ILeagueInfoScreenProps => {
  return {
    image: {imageSrc: {uri: league.icon_url}},
    title: league.title,
    description: league.description,
    winEarn: getWinEarnProps(league),
    timing: {endTime: new Date(league.ends_at)},
    ...callbacks,
  };
};

/**
 * Get the league information (macro for leveled leagues) props for one cell
 * @param league The league from the API
 * @param callbacks The callbacks on the league info macro UI
 * @returns The ILeagueInfoMacroCellProps
 */
export const getLeagueInfoMacroProps = (
  league: League,
  callbacks?: Pick<ILeagueInfoMacroCellProps, 'onPlayPress'>
): ILeagueInfoMacroCellProps => {
  return {
    image: {imageSrc: {uri: league.icon_url}},
    imageBackground: league.carousel[0]?.background_url
      ? {uri: league.carousel[0]?.background_url}
      : undefined,
    customColor: league.primary_color ?? undefined,
    prize: getPrizeInfo(league.total_prize_value[0]),
    time: {endTime: new Date(league.ends_at)},
    winEarn: getWinEarnProps(league),
    ...callbacks,
  };
};

//#region League Prizes

/**
 * Get's a player's prize for the current place (if it exists).
 * @param playerPlace place in which we want the prize.
 * @param prizes list of LeaguePrizeConfiguration objects to query
 * @returns the prize for the place if it is found, otherwise null
 */
export const getPrizeForPlace = (playerPlace: number, prizes: LeaguePrizeConfiguration[]) => {
  if (playerPlace <= 0 || !prizes?.length) {
    return null; //if the player's place is 0 or -1, mean the user didn't rank -> no prize
  }
  // Prizes in a league are always sorted by place in ascending numerical order. If a default prize
  // has been configured it will have a place of -1, and will always be the 0th element of the prizes array.
  const defaultPrizeConfigured = isPrizeDefault(prizes[0]);

  // Player has a configured prize if their place is less than or equal to last prize's place (whether unique or within prize range)
  const lastPrize = prizes[prizes.length - 1];
  const playerHasConfiguredPrize = isPrizeRange(lastPrize)
    ? playerPlace <= lastPrize.place_end
    : playerPlace <= lastPrize.place;

  if (!playerHasConfiguredPrize && !defaultPrizeConfigured) {
    return null; //if the place is after the last prize's place and there is no default prize -> no prize
  }

  if (!playerHasConfiguredPrize && defaultPrizeConfigured) {
    const defaultPrizeValue = prizes[0].value;
    return defaultPrizeValue; //if the place is after the last prize's place and there is a default prize -> show default prize
  }

  let prizeFound: LeaguePrizeValue | null = null;
  let prizeConfigIndex: number = 0;

  while (!prizeFound && prizeConfigIndex < prizes.length) {
    const prizeConfig = prizes[prizeConfigIndex];

    if (isPrizeUnique(prizeConfig) && prizeConfig.place === playerPlace) {
      //if we found it exactly
      prizeFound = prizeConfig.value;
    } else if (isPrizeRange(prizeConfig) && isPlayerPlacedInPrizeRange(playerPlace, prizeConfig)) {
      prizeFound = prizeConfig.value;
    }

    prizeConfigIndex++;
  }

  return prizeFound;
};

/**
 * This function back fills missing spots in the list of leagues from the api.  The logic is that if a prize or a place is missing then it's prize is the same as it's closest lower place with a prize.
 * !NOTE: This function is only used on non-progressive leagues leaderboard cells.
 * @param prizes list of prizes with possible missing items, note this list must be sorted by position from first - last place in order for this to work (that's how it comes from the api so shouldn't be an issue)
 * @returns another list of prizes with any gaps missing
 */
export const populateMissingPrizes = (prizes: {place: number; value: LeaguePrizeValue}[]) => {
  if (prizes.length > 1) {
    let runningIndex = prizes.length - 1; //end of list
    let lastPrize = prizes[runningIndex].value; //get last prize
    const newPrizes: {place: number; value: LeaguePrizeValue}[] = [];
    newPrizes.push({place: prizes[runningIndex].place, value: lastPrize});
    runningIndex--;
    let nextPlaceWithPrize = prizes[runningIndex].place;
    const startingPlace = prizes[prizes.length - 1].place;
    for (let n = startingPlace - 1; n > 0; n--) {
      // iterate from last place-1 to 0
      if (nextPlaceWithPrize === n) {
        // if the place matches the current n place make it the new prize
        lastPrize = prizes[runningIndex].value;
        runningIndex--;
        if (runningIndex >= 0) {
          // only grab the next one if there is one to grab, otherwise prizes[runningIndex] will be undefined
          nextPlaceWithPrize = prizes[runningIndex].place;
        }
      } else {
        if (n > nextPlaceWithPrize) {
          lastPrize = prizes[runningIndex].value;
        }
        //there is no case for n < nextPlaceWithPrize because it never should be
      }
      newPrizes.push({place: n, value: lastPrize});
    }
    return newPrizes;
  }
  return prizes;
};

/**
 * Get the list of empty prizes for appending the league response.
 * @param prizes The complete list of all prizes for a league.
 * @param listOfPlayersAsLeaderboardItems The current list of players as leaderboard items
 * @param fetchLimit The fetch limit used for the api calls
 * @param totalPlayers The total number of players in a league
 * @returns A filtered list of prizes to append to the end of the league response.
 * This function only returns the list of prizes when the listOfPlayersAsLeaderboardItems represents the last pages of users
 */
export const getListOfEmptyPrizes = (
  prizes: {place: number; value: LeaguePrizeValue}[],
  listOfPlayersAsLeaderboardItems: ILeagueLeaderboardCellProps[],
  fetchLimit: number,
  totalPlayers: number
) => {
  const isPartialApiResponse = listOfPlayersAsLeaderboardItems.length < fetchLimit;
  const atEndOfList =
    listOfPlayersAsLeaderboardItems.length >= fetchLimit &&
    listOfPlayersAsLeaderboardItems[listOfPlayersAsLeaderboardItems.length - 1].rank >=
      totalPlayers;
  if (isPartialApiResponse || atEndOfList) {
    const sortedPlayers =
      listOfPlayersAsLeaderboardItems.length > 0
        ? listOfPlayersAsLeaderboardItems.sort((firstEl, secondEl) => firstEl.rank - secondEl.rank)
        : [];
    const sortedPrizes = prizes.sort((firstEl, secondEl) => firstEl.place - secondEl.place);
    /**
     * lastPlace calculation explanation :
     * When there are players in the league, we should start with the last person's rank in the list.
     * When the list of players is zero we must check the total players to know where to start appending empty prizes.
     */
    const lastPlace =
      sortedPlayers.length > 0 ? sortedPlayers[sortedPlayers.length - 1].rank : totalPlayers ?? 0;
    const filteredPrizes = sortedPrizes.filter(
      (item: {place: number; value: LeaguePrizeValue}): boolean => {
        return item.place > lastPlace;
      }
    );
    return filteredPrizes;
  }
  /**
   * The only way time we want to append empty prizes to the list is if there is a partial
   * response or the rank of teh last user in the list is equal to the total number of players
   * all other cases we must return an empty list
   */
  return [];
};

export const isPrizeRange = (prize: LeaguePrizeConfiguration): prize is LeaguePrizeRange => {
  return !!prize && 'place_start' in prize && 'place_end' in prize && !('place' in prize);
};

export const isPrizeDefault = (prize: LeaguePrizeConfiguration): prize is LeaguePrizeDefault => {
  return !!prize && 'place' in prize && prize.place === -1;
};

export const isPrizeUnique = (prize: LeaguePrizeConfiguration): prize is LeaguePrizeUnique => {
  return !!prize && 'place' in prize && prize.place !== -1;
};

export const isPlayerPlacedInPrizeRange = (playerPlace: number, prize: LeaguePrizeRange) => {
  return playerPlace >= prize.place_start && playerPlace <= prize.place_end;
};

//#endregion League Prizes

/**
 * This function takes a LeaguePlayer, LeaguePrize and various other things necessary in order to create a LeaderboardItem which is used by a LeaderboardCell
 * @returns a fully formed leaderboard item
 */
export const getLeaderboardItem = (
  player: LeaguePlayer | undefined | null,
  prize: LeaguePrizeValue | undefined | null,
  rank: number,
  medalIcon: string,
  medalType?: ELeagueMedal,
  onPlayerPress?: () => void,
  onPrizePress?: () => void,
  isSelf?: boolean
): ILeagueLeaderboardCellProps => {
  const playerInfo = player
    ? {
        username: player.name,
        avatar: player.thumbnail_url ? {uri: player.thumbnail_url} : undefined, //the UI will take care of default avatar
        countryFlag: player.flag_url ? {uri: player.flag_url} : undefined,
        medalIcon: {uri: medalIcon},
        medalType: medalType,
        score: player.medals ? player.medals : 0,
        onPress: onPlayerPress,
        isSelf: !!isSelf,
      }
    : undefined;

  const prizeInfo = prize
    ? {
        info: getPrizeInfo(prize),
        onPress: onPrizePress,
      }
    : undefined;

  return {
    player: playerInfo,
    prize: prizeInfo,
    rank: rank || 0,
  };
};

/**
 * Get the league information (macro for leveled leagues) props for the page
 * @param league The league from the API
 * @param indexPreSelected The index which is selected (which the user is segmented to)
 * @param callbacks The callbacks on the league info macro UI
 * @returns The ILeagueInfoMacroScreenProps
 */
export const getLeagueInfoMacroListProps = (
  leagues: League[],
  indexPreSelected: number,
  callbacks: Pick<ILeagueInfoMacroCellProps, 'onPlayPress'> &
    Pick<ILeagueInfoMacroScreenProps, 'onClosePress'>
): ILeagueInfoMacroScreenProps => {
  return {
    items: leagues.map((league, index) =>
      index === indexPreSelected
        ? {
            cell: getLeagueInfoMacroProps(league, {onPlayPress: callbacks.onPlayPress}),
            description: league.description,
          }
        : {
            cell: getLeagueInfoMacroProps(league),
            description: league.description,
          }
    ),
    indexPreSelected,
    onClosePress: callbacks.onClosePress,
  };
};

/**
 * Get the league FTUE information props
 * @param leagues The league from the API
 * @param callbacks The callbacks on the league FTUE UI
 * @returns The ILeagueWelcomeScreenProps
 */
export const getLeagueFTUEProps = (
  leagues: League[]
): {pages: ILeagueWelcomeScreenProps['pages']} => {
  return {
    pages: leagues.map((league) => ({
      icon: {uri: league.icon_url},
      prize: getPrizeInfo(league.total_prize_value[0]),
      title: league.title,
      description: league.description,
    })),
  };
};

/**
 * Get the league Prize information props
 * @param callbacks The callbacks on the league Prize details UI
 * @returns The ILeaguePrizeInfoScreenProps
 */
export const getLeaguePrizeProps = (
  leaguePrize: LeaguePrizeValue,
  callbacks: Pick<ILeaguePrizeInfoScreenProps, 'onNextPress' | 'onBackPress'>
): ILeaguePrizeInfoScreenProps => {
  return {
    prize: getPrizeInfo(leaguePrize),
    ...callbacks,
  } as ILeaguePrizeInfoScreenProps;
};

/**
 * Transform the optional currency code given by the backend to something understood by the ui. (default to 'USD')
 * @param currencyCode the currency code given by the backend
 * @returns the currency code understood by ui
 */
const getCurrencyCode = (currencyCode?: string): TCurrencyCode => {
  if (!currencyCode) {
    return 'USD';
  }
  return currencyCode as TCurrencyCode;
};

/**
 * Get the Prize information
 * @param leaguePrize the league prize given by the backend
 * @returns the currency view with amount, currencyCode and optional isBonus
 */
export const getPrizeInfo = (
  leaguePrize: LeaguePrizeValue
): Pick<ICurrencyViewProps, 'amount' | 'currencyCode' | 'isBonus'> => {
  switch (leaguePrize.type) {
    case LeaguePrizeType.CASH:
      return {
        amount: leaguePrize.amount,
        currencyCode: getCurrencyCode(leaguePrize.currency),
      };
    case LeaguePrizeType.BONUS_CASH:
      return {
        amount: leaguePrize.amount,
        currencyCode: getCurrencyCode(leaguePrize.currency),
        isBonus: true,
      };
    case LeaguePrizeType.TICKETZ:
      return {
        amount: leaguePrize.amount,
        currencyCode: ECurrencyCode.TICKETZ,
      };
    case LeaguePrizeType.VIRTUAL_CURRENCY:
      return {
        amount: leaguePrize.amount,
        currencyCode: ECurrencyCode.VIRTUAL,
      };
    // case Custom //not in scope for now
  }
};

/**
 * Return the Multi Prize
 * @param league the league
 * @param carouselItem the carousel Item we are looking for
 * @returns the multi prize to show in the carousel
 */
const getMultiPrizes = (
  league: League,
  carouselItem: LeagueCarouselItem
): Pick<ILeagueLeaderboardCarouselCellProps, 'prizeTot'> => {
  const getTotPrizeForType = (type?: LeaguePrizeType | null) => {
    if (!type) {
      return undefined;
    }
    const prizeFound = league.total_prize_value.find((prize) => prize.type === type);
    if (prizeFound) {
      return getPrizeInfo(prizeFound);
    } else {
      if (type === LeaguePrizeType.CASH) {
        const altPrizeFound = league.total_prize_value.find(
          (prize) => prize.type === LeaguePrizeType.BONUS_CASH
        );
        if (altPrizeFound) {
          return getPrizeInfo(altPrizeFound);
        }
      }
    }
    return undefined;
  };
  if (
    !carouselItem.additional_fields?.show_prize_types ||
    carouselItem.additional_fields.show_prize_types.length === 0
  ) {
    //if no additional_fields.show_prize_types and no fallback to depreciated show_prize_type
    if (!carouselItem.show_prize_type) {
      return {};
    } else {
      return {
        prizeTot: getTotPrizeForType(carouselItem.show_prize_type),
      };
    }
  }
  const prizeTot: any[] = [];
  carouselItem.additional_fields.show_prize_types.forEach((value) => {
    const prizeFound = getTotPrizeForType(value);
    if (prizeFound) {
      prizeTot.push(prizeFound);
    }
  });

  return {
    prizeTot: prizeTot.length > 0 ? prizeTot : getTotPrizeForType(carouselItem.show_prize_type),
  };
};
/**
 * Get the league carousel information props for the cell
 * @param league The league from the API
 * @param leagueCarouselItem The league carousel item from the API
 * @returns the ILeagueLeaderboardCarouselCellProps
 */
export const getLeagueCarouselCellProps = (
  league: League,
  leagueCarouselItem: LeagueCarouselItem
): ILeagueLeaderboardCarouselCellProps => {
  return {
    backgroundImage:
      (leagueCarouselItem.background_url?.trim()?.length ?? 0) > 0
        ? {uri: leagueCarouselItem.background_url}
        : undefined,
    icon: leagueCarouselItem.icon_url ? {imageSrc: {uri: leagueCarouselItem.icon_url}} : undefined,
    label: leagueCarouselItem.show_title ? {label: league.title} : undefined,
    title: leagueCarouselItem.subtitle || undefined,
    subtitle: leagueCarouselItem.description,
    iconPlacement: league.type === LeagueType.CUSTOM ? 'bottom' : 'center',
    ...getMultiPrizes(league, leagueCarouselItem),
  };
};

/**
 * Get the league carousel props
 * @param league The league from the API
 * @param callbacks The callbacks on the league carousel UI (including cell's callback)
 * @returns The ILeagueLeaderboardCarouselProps
 */
export const getLeagueCarouselProps = (
  league: League,
  callbacks: Pick<ILeagueLeaderboardCarouselProps, 'onPlayPress'> &
    Pick<ILeagueLeaderboardCarouselCellProps, 'onInfoPress'>
): ILeagueLeaderboardCarouselProps => {
  return {
    items: league.carousel.map((item, index) => ({
      ...getLeagueCarouselCellProps(league, item),
      onInfoPress: callbacks.onInfoPress,
      shadow: league.type === LeagueType.CUSTOM ? (index === 0 ? 'top' : 'full') : 'start',
    })),
    timeline: {endTime: new Date(league.ends_at)},
    onPlayPress: callbacks.onPlayPress,
  };
};

/**
 * Get the league prize result props
 * @param league The league from API
 * @param player The current player
 * @param prize The prize that the player wins
 * @returns ILeagueResultPrizePageProps`
 */
export const getLeaguePrizeResultProps = (
  league: League,
  player: LeaguePlayer,
  prize: LeaguePrizeValue
): ILeagueResultPrizePageProps => {
  const getPrizeDescriptionForEachRank = () => {
    return player.place === 1
      ? _m(
          'You are the season champion for {0}! Enjoy the glory that comes with being the very best!',
          league.title
        )
      : _m(
          'You have placed in {0} place for {1}! Reap the rewards that comes with being one of the top players in this League',
          I18n.getOrdinalString(player.place),
          league.title
        );
  };

  const prizeInfo = getPrizeInfo(prize);

  return {
    title: prizeInfo,
    prizeImage: getInfoFromCurrencyCode({
      currencyCode: prizeInfo.currencyCode,
      theme: SkillzStyle.themeJSON,
    }).getIcon({themeType: getLeagueTheme(league).mode, isBonus: prizeInfo.isBonus}) || {uri: ''},
    userAvatar: {uri: getProfileImageFor(player)},
    position: player.place,
    description: getPrizeDescriptionForEachRank(),
    footerNote: league.title,
    endDate: new Date(league.ends_at),
  };
};

// eslint-disable-next-line no-magic-numbers
type rankType = 1 | 2 | 3;

/**
 * Get info for each item in League Podium
 * @param league the given league from API
 * @param player the current play
 * @param prize the prize that the current player wins
 * @returns ILeaguePodiumItemProps
 */
export const getLeaguePodiumItemProps = (
  league: League,
  rank: rankType,
  isHighlighted: boolean,
  player?: LeaguePlayer,
  prize?: LeaguePrizeValue | null,
  callbacks?: Pick<ILeaguePodiumItemProps, 'onPress'>
): ILeaguePodiumItemProps => {
  //TODO default view if no prize or no player
  return {
    prize: prize ? getPrizeInfo(prize) : 'No prize',
    avatar: player ? getProfileImageFor(player) : undefined,
    username: player?.name || '',
    medalIcon: league.medal.url,
    medalType: getMedalType(league.type),
    rank,
    isHighlighted,
    medalAmount: player?.medals ?? 0,
    onPress: player && callbacks?.onPress,
  };
};

//adding 1 to account for list indexes starting at 0
const getPodiumRankFromIndex = (index: number): rankType => {
  // eslint-disable-next-line no-magic-numbers
  if (index >= 0 && index < 3) {
    return (index + 1) as rankType;
  }
  return 1;
};

/**
 * Get the top three winner for Podium
 * @param league the given league from API
 * @param players the list of each item in podium which contain player and the prize (coming from league arg)
 * @param currentPlayer the current Player to know which podium item to highlight
 * @returns ILeaguePodiumViewProps
 */
export const getLeaguePodiumViewProps = (
  league: League,
  players: {player: LeaguePlayer; callbacks?: Pick<ILeaguePodiumItemProps, 'onPress'>}[], // assume that list is sorted
  currentPlayer: LeaguePlayer
): ILeaguePodiumViewProps => {
  const getItemAtIndex = (index: number): ILeaguePodiumItemProps => {
    const isHighlighted = currentPlayer.user_id === players[index]?.player.user_id;

    return {
      ...getLeaguePodiumItemProps(
        league,
        getPodiumRankFromIndex(index),
        isHighlighted,
        players[index]?.player,
        getPrizeForPlace(index + 1, league.prizes),
        players[index]?.callbacks
      ),
    };
  };

  return {
    title: league.title,
    firstPlace: getItemAtIndex(0),
    secondPlace: getItemAtIndex(1),
    thirdPlace: getItemAtIndex(2),
  };
};

/**
 * Get the league info to show on the locked screen
 * @param league the given league from the API
 * @param callback the call back function
 * @returns ILeagueLockedScreenProps
 */
export const getLeagueLockedScreenInfo = (
  league: League,
  callback: Pick<ILeagueLockedScreenProps, 'onCTAPress'>
): ILeagueLockedScreenProps => {
  const getPrizeTotalFromLeague = league.total_prize_value.map((i) => {
    return getPrizeInfo(i);
  });

  return {
    numToUnlock: league.games_to_unlock,
    totalToUnlock: league.total_games_to_unlock,
    totalPrizes: getPrizeTotalFromLeague,
    onCTAPress: callback.onCTAPress,
  };
};

/**
 * Get the league info to show on the locked dialog
 * @param league the given league from the API
 * @param callback the call back function
 * @returns ILeagueLockedScreenProps
 */
export const getLeagueLockedDialogInfo = (
  leagues: League[],
  callback: Pick<ILeagueLockedScreenProps, 'onCTAPress'>
): ILeagueLockedScreenProps => {
  //accumulated all prize of all leagues
  let currencyCode = '';
  const prizeTotalFromLeagues = leagues.reduce<{cash: number; zAmount: number; ticketz: number}>(
    (prev, cur) => {
      cur.total_prize_value.forEach((prize) => {
        if (prize.type === LeaguePrizeType.BONUS_CASH || prize.type === LeaguePrizeType.CASH) {
          prev.cash += prize.amount;
          currencyCode = prize.currency || currencyCode;
        }
        if (prize.type === LeaguePrizeType.VIRTUAL_CURRENCY) {
          prev.zAmount += prize.amount;
        }
        if (prize.type === LeaguePrizeType.TICKETZ) {
          prev.ticketz += prize.amount;
        }
      });
      return prev;
    },
    {cash: 0, zAmount: 0, ticketz: 0}
  );

  //find the closest league to be unlocked (initialize in case leagues array is empty)
  const league = leagues.reduce<{games_to_unlock: number; total_games_to_unlock: number}>(
    (prev, cur) =>
      cur.games_to_unlock < prev.games_to_unlock
        ? {games_to_unlock: cur.games_to_unlock, total_games_to_unlock: cur.total_games_to_unlock}
        : prev,
    {games_to_unlock: 5, total_games_to_unlock: 5}
  );

  return {
    numToUnlock: league.games_to_unlock,
    totalToUnlock: league.total_games_to_unlock,
    totalPrizes: [
      {
        amount: prizeTotalFromLeagues.cash,
        currencyCode,
      },
      {
        amount: prizeTotalFromLeagues.zAmount,
        currencyCode: ECurrencyCode.VIRTUAL,
      },
      {
        amount: prizeTotalFromLeagues.ticketz,
        currencyCode: ECurrencyCode.TICKETZ,
      },
    ],
    onCTAPress: callback.onCTAPress,
  };
};

/**
 * Get the league player profile image
 * @param player the given league player
 * @returns url to the profile image or the default user png
 */
export const getProfileImageFor = (user: LeaguePlayer | null | undefined): string => {
  return user && user.thumbnail_url ? user.thumbnail_url : 'empty_user.png';
};

export const getMedalIcon = (hasCash: boolean, highRes?: boolean) => {
  if (ClientPropertyConstants.getLeaguesV3Enabled()) {
    return hasCash
      ? 'https://cdn.skillz.com/portal-admin/gold-medal.png'
      : 'https://cdn.skillz.com/portal-admin/silver-medal.png';
  } else if (highRes) {
    return hasCash
      ? 'ic_cash_leaderboard_hi_res_medal_count_skz.png'
      : 'ic_z_leaderboard_hi_res_medal_count_skz.png';
  } else {
    return hasCash
      ? 'ic_cash_leaderboard_lo_res_medal_count_skz.png'
      : 'ic_z_leaderboard_lo_res_medal_count_skz.png';
  }
};

/**
 * Get the type of medal to represent the league
 * @param leagueType the type of the league
 * @returns the type of medal for the league
 */
export const getMedalType = (leagueType: LeagueType): ELeagueMedal | undefined => {
  if (!ClientPropertyConstants.getLeaguesCombinedMedalsEnabled()) {
    return undefined;
  }

  if (leagueType === LeagueType.MEDAL) {
    return ELeagueMedal.UNIFIED;
  }
  if (leagueType === LeagueType.Z) {
    return ELeagueMedal.SILVER;
  }
  return ELeagueMedal.GOLD;
};

/**
 * Checks if the league is a progressive league
 * @param league
 * @returns boolean
 */
export const isProgressiveLeague = (league: League | undefined) => {
  return Boolean(league && league?.progressive_league);
};

/**
 * Checks if the league is a progressive league that requires a service fee to join
 * @param league
 * @returns boolean
 */
export const isPaidProgressiveLeague = (league: League | undefined) => {
  return Boolean(
    league &&
      league?.progressive_league &&
      league?.progressive_league.service_fees &&
      league?.progressive_league.service_fees.length > 0
  );
};

/**
 * Checks if a league set has a paid progressive league
 * @param leagues The league set to check for paid progressive leagues
 * @returns boolean
 */
export const hasPaidProgressiveLeague = (leagues: League[] | undefined) => {
  return leagues?.some((league) => isPaidProgressiveLeague(league)) ?? false;
};

/**
 * Map the progressive league player as a leaderboard player cell
 * @param leaguePlayer
 * @param leagueMedalType
 * @param prizeForPlace
 * @param onPlayerPress
 * @returns IProgressiveLeagueLeaderboardPlayerCell
 * @example
 * const progressiveLeagueLeaderboardPlayerCell = mapProgressiveLeaderboardPlayer(leaguePlayer, leagueMedalType, prizeForPlace, onPlayerPress);
 * const { player, rank, prize } = progressiveLeagueLeaderboardPlayerCell;
 */
export const mapProgressiveLeaderboardPlayer = (
  leaguePlayer: LeaguePlayer,
  leagueMedalType: ELeagueMedal | undefined,
  prizeForPlace: LeaguePrizeValue | null,
  onPlayerPress?: () => void
): IProgressiveLeagueLeaderboardPlayerCell => {
  const avatarUri: ImageSourcePropType = {uri: leaguePlayer.thumbnail_url};
  const countryFlagUri: ImageSourcePropType = {uri: leaguePlayer.flag_url};
  const avatarFrameUri = AssetHelper.pathForAssetNamed(getTierImage(leaguePlayer.tier_id), false);
  return {
    player: {
      username: leaguePlayer.name,
      medalCount: leaguePlayer.medals || 0,
      avatar: avatarUri,
      countryFlag: countryFlagUri,
      medalType: leagueMedalType,
      avatarFrame: ClientPropertyConstants.getLoyaltyProfileFramesEnabled()
        ? {uri: avatarFrameUri}
        : undefined,
    },
    onPress: onPlayerPress,
    rank: leaguePlayer.place,
    ...(prizeForPlace && {
      prize: {
        info: getPrizeInfo(prizeForPlace),
      },
    }),
  };
};

/**
 * Checks the diff between two leagues and determines if the user has been awarded an Instant Promotion.
 * @param oldLeague User's "previous" or "stale" League. Most likely coming from local storage.
 * @param newLeague User's current League. Most likely coming from a just completed API call.
 * @returns boolean. True if user has been instant promoted, false if not.
 */
export const hasUserBeenInstantPromoted = (oldLeague: League | null, newLeague: League | null) => {
  const oldProgressiveLeague = oldLeague?.progressive_league;
  const newProgressiveLeague = newLeague?.progressive_league;
  // If either league is not a Progressive League, then you should not be using this function
  if (!oldProgressiveLeague || !newProgressiveLeague) {
    return false;
  }

  // If either the current or old league is a paid league, then you should not show the instant promotion modal
  if (isPaidProgressiveLeague(oldLeague) || isPaidProgressiveLeague(newLeague)) {
    return false;
  }

  // If the occurrence numbers between the two Leagues are the same, and the new League's tier is higher
  // than the old League's tier, then we know you've been Instant Promoted.
  if (
    oldProgressiveLeague.occurrence_number === newProgressiveLeague.occurrence_number &&
    newProgressiveLeague.tier > oldProgressiveLeague.tier
  ) {
    return true;
  }

  // Otherwise, you haven't been Instant Promoted.
  return false;
};

/**
 * Checks if two leagues have the same league_set_configuration_id and the same progressive_league.occurrence_number.
 * @param league1 First league to compare.
 * @param league2 Second league to compare.
 * @returns boolean. True if the leagues meet both conditions, false otherwise.
 */
export const haveSameLeagueConfigurationAndOccurrence = (
  league1: League | null,
  league2: League | null
) => {
  if (!league1 || !league2) {
    return false;
  }
  if (!league1.progressive_league || !league2.progressive_league) {
    return false;
  }
  const sameConfiguration =
    league1.league_set_configuration_id === league2.league_set_configuration_id;
  const sameOccurrenceNumber =
    league1.progressive_league?.occurrence_number === league2.progressive_league?.occurrence_number;
  const result = sameConfiguration && sameOccurrenceNumber;
  return result;
};

/**
 * Checks if the `progressive_league.tier` of the first league is less than that of the second league.
 * @param league1 First league to compare.
 * @param league2 Second league to compare.
 * @returns boolean. True if the first league's tier is less than the second league's tier, false otherwise.
 */
export const isProgressiveLeagueTierLessThan = (league1: League, league2: League): boolean => {
  if (!league1 || !league2 || !league1.progressive_league || !league2.progressive_league) {
    return false;
  }
  return league1.progressive_league.tier < league2.progressive_league.tier;
};

/**
 * Handles updating or saving a progressive league to local storage if needed.
 * @param currentLeague The league to handle.
 * @returns The saved or updated league, or `null` if not applicable.
 */
export const syncProgressiveLeagueStorage = async (
  currentLeague: League
): Promise<League | null> => {
  const isProgressive = isProgressiveLeague(currentLeague);
  if (!isProgressive) {
    return null;
  }
  const savedLeague = await getLeagueFromLocal();
  if (!savedLeague) {
    saveLeagueToLocal(currentLeague);
    return currentLeague;
  } else {
    // If the league configuration or occurrence has changed, update local storage
    // If the current league tier is less than the saved league tier, then update local storage
    if (
      !haveSameLeagueConfigurationAndOccurrence(savedLeague, currentLeague) ||
      isProgressiveLeagueTierLessThan(currentLeague, savedLeague)
    ) {
      resetLeaguesInterstitialSkipCount();
      saveLeagueToLocal(currentLeague);
      saveLeaguePlayerDataToLocal(null);
      return currentLeague;
    } else {
      return savedLeague;
    }
  }
};

/**
 * Save a League object to local storage.
 * @param league The league object to save.
 */
export const saveLeagueToLocal = async (league: League | null) => {
  try {
    const leagueJson = league !== null ? JSON.stringify(league) : 'null';
    await AsyncStorage.setItem('savedLeague', leagueJson);
  } catch (error) {
    console.error('Error saving league to local storage:', error);
  }
};

/**
 * Get a League object from local storage.
 * @returns The league object retrieved from local storage, or null if not found.
 */
export const getLeagueFromLocal = async (): Promise<League | null> => {
  try {
    const leagueJson = await AsyncStorage.getItem('savedLeague');
    if (leagueJson) {
      return JSON.parse(leagueJson);
    }
    return null;
  } catch (error) {
    console.error('Error getting league from local storage:', error);
    return null;
  }
};

/**
 * Save a GetLeaguePlayerResult object to local storage.
 * @param playerData The GetLeaguePlayersResult object to save.
 */
export const saveLeaguePlayerDataToLocal = async (playerData: SavedLeagueCurrentPlayerData) => {
  try {
    // We only need the currentPlayer information, so we avoid saving the entire object to avoid flooding local storage
    const playerDataToStore =
      playerData !== null ? {current_player: playerData.current_player} : null;

    const playerDataJson = JSON.stringify(playerDataToStore);
    await AsyncStorage.setItem('savedLeaguePlayerData', playerDataJson);
  } catch (error) {
    console.error('Error saving league player data to local storage:', error);
  }
};

/** Get a GetLeaguePlayerResult object from local storage
 * @returns The league player data retrieved from local storage, or null if not found.
 */
export const getLeaguePlayerDataFromLocal = async (): Promise<SavedLeagueCurrentPlayerData> => {
  try {
    const playerDataJson = await AsyncStorage.getItem('savedLeaguePlayerData');
    if (playerDataJson) {
      return JSON.parse(playerDataJson) as SavedLeagueCurrentPlayerData;
    }
    return null;
  } catch (error) {
    console.error('Error getting league player data from local storage:', error);
    return null;
  }
};

/**
 * Determines if a player is on the leaderboard based on the player data.
 * @param playerData
 * @returns
 */
export const playerIsOnLeaderboard = (playerData: GetLeaguePlayersResult | null) => {
  return (
    playerData &&
    playerData?.current_player &&
    playerData?.current_player?.medals !== 0 &&
    playerData?.current_player?.place !== 0
  );
};

/** Determines the correct icon to show for progressive leagues based on the prize type */
export const getProgressiveLeaguesIconForPrizeType = (prizeType: LeaguePrizeType) => {
  const isLeaguesV3UIV2Enabled = ClientPropertyConstants.getLeaguesV3UIV2Enabled();

  switch (prizeType) {
    case LeaguePrizeType.CASH:
    case LeaguePrizeType.BONUS_CASH:
      return isLeaguesV3UIV2Enabled ? 'ic_leagues_cash_coin.png' : 'ic_cash_usd_background.png';
    case LeaguePrizeType.TICKETZ:
      return isLeaguesV3UIV2Enabled ? 'ic_leagues_ticketz_coin.png' : 'ic_ticketz_background.png';
    case LeaguePrizeType.VIRTUAL_CURRENCY:
      return isLeaguesV3UIV2Enabled
        ? 'ic_leagues_z_coin.png'
        : SkillzStyle.currencyValues.practiceCurrencyFullIconLargeURI;
    default:
      return '';
  }
};

/** Takes the total prize values of a progressive league and returns the same, but combines cash and bonus cash together, as we don't currently support the two as distinct currencies. If this ever changes, this function will no longer be necessary. */
export const formatProgressiveLeaguesTotalPrizeValues = (league: League): LeaguePrizeValue[] => {
  let totalCashPrizes = 0;
  let totalTicketzPrizes = 0;
  let totalZPrizes = 0;
  let currency = 'USD';

  league.total_prize_value.forEach((leaguePrize: LeaguePrizeValue) => {
    if (
      leaguePrize.type === LeaguePrizeType.CASH ||
      leaguePrize.type === LeaguePrizeType.BONUS_CASH
    ) {
      totalCashPrizes += leaguePrize.amount;
      currency = leaguePrize.currency || 'USD';
    }
    if (leaguePrize.type === LeaguePrizeType.TICKETZ) {
      totalTicketzPrizes += leaguePrize.amount;
    }
    if (leaguePrize.type === LeaguePrizeType.VIRTUAL_CURRENCY) {
      totalZPrizes += leaguePrize.amount;
    }
  });

  const totalPrizeValues: LeaguePrizeValue[] = [];
  if (totalCashPrizes > 0) {
    totalPrizeValues.push({
      type: LeaguePrizeType.CASH,
      amount: totalCashPrizes,
      currency: currency,
    });
  }
  if (totalTicketzPrizes > 0) {
    totalPrizeValues.push({type: LeaguePrizeType.TICKETZ, amount: totalTicketzPrizes});
  }
  if (totalZPrizes > 0) {
    totalPrizeValues.push({type: LeaguePrizeType.VIRTUAL_CURRENCY, amount: totalZPrizes});
  }

  return totalPrizeValues;
};

/**
 * Function that looks at the tiers of the concluded and current leagues, and determines if the user was
 * promoted, safe, or demoted. Note that lowest tier is 0, and higher tiers are positive numbers
 * @param concludedLeague
 * @param currentLeague
 * @returns LeagueResultsProgression
 */
export const getLeagueTierChange = (
  concludedLeague: League,
  currentLeague: League
): LeagueResultsProgression => {
  const concludedLeagueTier = concludedLeague.progressive_league!.tier;
  const currentLeagueTier = currentLeague.progressive_league!.tier;
  const leagueTierDiff = currentLeagueTier - concludedLeagueTier;
  if (leagueTierDiff > 0) {
    return 'PROMOTED';
  } else if (leagueTierDiff < 0) {
    return 'DEMOTED';
  } else {
    return 'SAFE';
  }
};

/**
 * Determines the correct opting button type for the header based on the league set and the player's tier.
 * @param doesLeagueSetHavePaidTier - Whether the league set has a paid tier.
 * @param isPlayerInPaidTier - Whether the player is in the paid tier.
 * @param userCanOptIn - Whether the user can opt in to the paid tier.
 * @param isViewingPaidTier - Whether the user is viewing the paid tier.
 * @returns The opting button type.
 *  - NONE: Don't show the button
 *  - OPT-IN: Show the opt-in button
 *  - OPT-OUT: Show the opt-out button
 */
export const getOptingButtonTypeForHeader = (
  doesLeagueSetHavePaidTier: boolean,
  isPlayerInPaidTier: boolean,
  userCanOptIn: boolean,
  isViewingPaidTier: boolean
): OptingButtonType => {
  // If no paid tier in set, don't show the button
  if (!doesLeagueSetHavePaidTier) {
    return 'NONE';
  }
  // If the user is not viewing the paid tier, then we should not show the opt-in button
  if (!isViewingPaidTier) {
    return 'NONE';
  }
  // If player is in paid tier, show the opt-out button
  if (isPlayerInPaidTier) {
    return 'OPT-OUT';
  }
  // If we have a paid tier in the set and the user is not in the paid tier, check if the user actually can opt in. We only allow one opt-in per league duration.
  if (!userCanOptIn) {
    return 'NONE';
  }
  // If the user can opt in, show the opt-in button
  return 'OPT-IN';
};

/**
 * Returns the service fees string for a league, to be used in the paid league modals.
 * @param league The league to get the service fees string for.
 * @returns The service fees string for the league.
 */
export const getServiceFeesStringFromLeague = (league: League) => {
  const serviceFees = league.progressive_league?.service_fees;
  if (!serviceFees || serviceFees.length === 0) {
    return '';
  }

  const feePercentages = serviceFees.map((fee) => fee.fee_percentage);
  const minFeePercentage = Math.min(...feePercentages);
  const maxFeePercentage = Math.max(...feePercentages);

  // If min and max are the same, show just one value
  if (minFeePercentage === maxFeePercentage) {
    return `${minFeePercentage}%`;
  }

  // Otherwise show the range
  return `${minFeePercentage}% to ${maxFeePercentage}%`;
};

//#region Leagues Interstitials Methods - START

/**
 * Get current league data from the API
 */
export const fetchCurrentLeague = async (): Promise<League | null> => {
  if (Platform.OS === 'web') {
    // Leagues not implemented on Web.
    return;
  }
  const queryClient = new QueryClient();
  try {
    const data = await queryClient.fetchQuery({
      queryKey: queryKeysLeagues.getLeagues,
      queryFn: () => getLeagues({concluded: false}),
      retryDelay: 500,
    });
    const [currentLeague] = data?.leagues ?? [];
    return isProgressiveLeague(currentLeague) ? currentLeague : null;
  } catch (error) {
    return null;
  }
};

/**
 * Fetch league players data from the API
 */
export const fetchCurrentUserLeaderboardData = async (
  league: League,
  savedPlayerPlace: number
): Promise<GetLeaguePlayersResult | null> => {
  // Get league that matches the passed in type
  const instId = league.league_instance_id;
  if (instId) {
    try {
      let playerResult: GetLeaguePlayersResult;
      if (savedPlayerPlace === 0) {
        // We will get 0 here if we don't have saved player data yet or we've been instantly promoted and don't need the saved player data, so just give us the minimal amount of data so we can use/store the current_player
        playerResult = await getLeaguePlayers({
          league_instance_id: instId,
          limit: 1,
        });
      } else {
        // Otherwise, we have saved player data, use the new previousPlace prop to tell server to only return the specific players we need. See GetLeaguePlayers and ProgressiveLeaguesStatusScreen.utils for more specific details on how this works.
        playerResult = await getLeaguePlayers({
          league_instance_id: instId,
          previousPlace: savedPlayerPlace,
        });
      }
      return playerResult;
    } catch (error) {
      console.debug('League Interstitial Log: Error fetching league player data:', error);
      return null;
    }
  }
  return null;
};

/**
 * Fetch league set data from the API
 */
export const fetchCurrentLeagueSet = async (
  league: League
): Promise<GetLeagueSetsResult | null> => {
  const leagueSetConfigId = league?.league_set_configuration_id;
  if (leagueSetConfigId) {
    const queryClient = new QueryClient();
    try {
      const data = await queryClient.fetchQuery({
        queryKey: [
          queryKeysLeagueSet.getLeagueSet,
          leagueSetConfigId,
          league?.progressive_league?.occurrence_number,
        ],
        queryFn: () =>
          getLeagueSets({
            league_set_configuration_id: leagueSetConfigId,
            concluded: false,
          }),
      });

      return data;
    } catch (error) {
      console.debug('League Interstitial Log: Error fetching league set data:', error);
      return null;
    }
  }
  return null;
};

/**
 * Save the league data to local storage
 * @param league The league data to save
 */
export const saveLeagueToLocalStorage = async (league: League) => {
  if (league && isProgressiveLeague(league)) {
    try {
      await syncProgressiveLeagueStorage(league);
    } catch (error) {
      console.error('League Interstitial Log: Error syncing league data:', error);
    }
  }
};

/**
 * Reset the league interstitial skip count to 1
 */
export const resetLeaguesInterstitialSkipCount = () => {
  Prefs.set({[SKZConstants.SKZ_LEAGUES_STATUS_INTERSTITIAL_SKIPS]: 1});
};

/**
 * This method is used to determine if the user should be shown the league results interstitial.
 * The interstitial is shown based on the number of skips the user has made.
 * The user is shown the interstitial after a certain number of skips.
 * The number of skips is reset to 1 after the interstitial is shown.
 * @param league - The current league data
 * @returns boolean - True if the interstitial should be shown, false otherwise
 * */
export const showUserLeaguesInterstitialStatus = () => {
  const maxSkips = ClientPropertyConstants.getLeaguesStatusInterstitialMaxSkips();
  let currentSkip = parseInt(Prefs.get(SKZConstants.SKZ_LEAGUES_STATUS_INTERSTITIAL_SKIPS)) || 1;
  if (currentSkip < maxSkips) {
    //Increment the skip count and store it locally
    Prefs.set({[SKZConstants.SKZ_LEAGUES_STATUS_INTERSTITIAL_SKIPS]: currentSkip + 1});
    return false;
  } else {
    // Return true and let UI component clean up the stored data
    return true;
  }
};

/**
 * This method is used to handle league interstitials promotion and sync leagues data to local storage.
 * @returns LeagueInterstitialResult | null
 * Steps:
 * 1. Fetch saved league data if any stored locally.
 * 2. Fetch current league data.
 * 3. Determine if the user has been instantly promoted using the saved and current league data.
 * 4. If the user has been instantly promoted, return early with instant promotion data.
 * 5. Check if a new leagues has started by comparing the saved and current league data.
 * 6. If new leagues has started reset the skip count. Save the current league to local and return early.
 * 7. Get the saved league player data if any stored locally.
 * 8. Fetch the current league player data.
 * 9. If the player is not on the leaderboard then reset everything and return early.
 * 10. If we have no player data, then we need to save it to create a diff between past and previous league rank data. We are very diligent about clearing out this data when any of the flows are hit where the data should be reset (instantly promoted, new league, etc).
 * 11. Fetch current league set data.
 * 12. Determine if the league results interstitial should be shown to the user.
 * 13. Save the current league locally.
 */
export const handleLeagueInterstitialsAndSync =
  async (): Promise<LeagueInterstitialResult | null> => {
    const savedLeagueRef = await getLeagueFromLocal();
    const currentLeagueRef = await fetchCurrentLeague();
    if (!currentLeagueRef) {
      return null;
    }
    const showLeagueInstantPromotionInterstitial = hasUserBeenInstantPromoted(
      savedLeagueRef,
      currentLeagueRef
    );

    if (showLeagueInstantPromotionInterstitial) {
      const currentLeaguePlayerData = await fetchCurrentUserLeaderboardData(currentLeagueRef, 0);
      return {
        savedLeagueRef,
        currentLeagueRef,
        showLeagueInstantPromotionInterstitial,
        currentLeaguePlayerData,
      };
    }
    const hasNewLeaguesStarted = !haveSameLeagueConfigurationAndOccurrence(
      savedLeagueRef,
      currentLeagueRef
    );

    if (hasNewLeaguesStarted) {
      resetLeaguesInterstitialSkipCount();
      await saveLeagueToLocalStorage(currentLeagueRef);
      saveLeaguePlayerDataToLocal(null);
      return {savedLeagueRef, currentLeagueRef, showLeagueInstantPromotionInterstitial};
    }

    const savedLeaguePlayerData: SavedLeagueCurrentPlayerData =
      await getLeaguePlayerDataFromLocal();

    const savedPlayerPlace = savedLeaguePlayerData?.current_player?.place ?? 0;

    const currentLeaguePlayerData = await fetchCurrentUserLeaderboardData(
      currentLeagueRef,
      savedPlayerPlace
    );

    if (!playerIsOnLeaderboard(currentLeaguePlayerData)) {
      resetLeaguesInterstitialSkipCount();
      await saveLeagueToLocalStorage(currentLeagueRef);
      saveLeaguePlayerDataToLocal(null);
      return {savedLeagueRef, currentLeagueRef, showLeagueInstantPromotionInterstitial};
    }

    if (!savedLeaguePlayerData) {
      await saveLeaguePlayerDataToLocal(currentLeaguePlayerData);
    }

    const currentLeagueSetData = await fetchCurrentLeagueSet(currentLeagueRef);

    const showLeagueStatusInterstitial = showUserLeaguesInterstitialStatus();
    await saveLeagueToLocalStorage(currentLeagueRef);

    return {
      savedLeagueRef,
      currentLeagueRef,
      showLeagueInstantPromotionInterstitial,
      showLeagueStatusInterstitial,
      savedLeaguePlayerData,
      currentLeaguePlayerData,
      currentLeagueSetData,
    };
  };

//#endregion Leagues Interstitials Methods - END

export type LeagueResultsProgression = 'PROMOTED' | 'SAFE' | 'DEMOTED';
/**
 * Function to get the header text for the league Conclude/Results screen
 * @param tierChange Function to get the header text for the league tier change
 * @returns string
 */
export const getLeagueTierHeader = (tierChange: LeagueResultsProgression): string => {
  switch (tierChange) {
    case 'PROMOTED':
      return _.sample([
        _m('Rising to Greatness!'),
        _m('Onward & Upward!'),
        _m('Promotion Secured!'),
        _m("You've Broken Through!"),
        _m("You've Climbed Higher!"),
        _m('Next League, Next Legacy!'),
        _m('Your Ascent Begins!'),
        _m('Stepping Into Greatness!'),
        _m('Unlocked: Higher Tier!'),
        _m('League Upgraded!'),
      ]);
    case 'SAFE':
      return _.sample([
        _m('Steady & Strong!'),
        _m('Consistency Wins!'),
        _m('Unshaken, Unstoppable!'),
        _m('Built for the Long Run!'),
        _m('Proven & Persistent!'),
        _m('Unwavering & Unyielding!'),
        _m('Holding Strong!'),
        _m('Unmoved, Unbreakable!'),
        _m('Solid & Steady!'),
        _m('Consistent & Climbing!'),
      ]);
    case 'DEMOTED':
      return _.sample([
        _m('Stage Your Comeback!'),
        _m("Champions Don't Quit!"),
        _m('Rise Again!'),
        _m('Bounce Back!'),
        _m('Prove Your Worth!'),
        _m('Climb Back Up!'),
        _m('Redemption Awaits!'),
        _m('Not Over Yet!'),
        _m('Legends Rise Again!'),
        _m('Make Your Return!'),
      ]);
  }
};

/**
 * Function to get the subheader text for the league Conclude/Results screen
 * @param tierChange
 * @returns string | undefined
 */

export const getLeaguesTierSubHeader = (tierChange: LeagueResultsProgression): string => {
  switch (tierChange) {
    case 'PROMOTED':
      return _.sample([
        _m('You played, you slayed, you leveled up!'),
        _m('Too strong, too skilled—next league had to take you!'),
        _m('You outplayed, outscored, and outleveled!'),
        _m('Your game’s on fire—higher league unlocked!'),
        _m('Skills so sharp, they cut through tiers!'),
        _m('Promoted! Because winning is your habit.'),
        _m('Too good for the old league—onward!'),
        _m('Your talent couldn’t be contained—up you go!'),
        _m('You just made the leaderboard jealous!'),
        _m('Your skills refused to stay put—so up you go!'),
      ]);
    case 'SAFE':
      return _.sample([
        _m('You held your ground—reap the rewards!'),
        _m('Consistency pays off. Your rewards await!'),
        _m('You’re holding strong. Now aim even higher!'),
        _m('Well played! Keep the momentum going.'),
        _m('Stability is strength. Enjoy your rewards!'),
        _m('No fallbacks, no setbacks—just steady wins!'),
        _m('You’ve secured your spot. Now, go for more!'),
        _m('Consistent play, consistent wins!'),
        _m('You’ve held strong—now aim higher!'),
        _m('Persistence pays off—your journey continues!'),
      ]);
    case 'DEMOTED':
      return _.sample([
        _m('A fresh start, a new chance to conquer!'),
        _m('The road back to the top starts here!'),
        _m('A big comeback will earn big rewards.'),
        _m('Every fall is a setup for a greater comeback.'),
        _m('New tier, new prizes, new opportunities!'),
        _m('The league is over, but your comeback starts now.'),
        _m('A new tier, a new challenge—rise again!'),
        _m('Bounce back stronger in the next season!'),
        _m('A setback today, a comeback tomorrow!'),
        _m('Learn. Adapt. Dominate the next league!'),
      ]);
  }
};

//#region Functions for Automatic Prompt of Paid League Opt In Modal - START

export const shouldShowAutomaticPromptForPaidLeagueOptInModal = (
  flow: PaidLeagueOptInModalFlow,
  leagueId: string
) => {
  const maxTriggersPerSeason =
    ClientPropertyConstants.getLeaguesPaidLeaguePromptMaxTriggersSeason();
  // If we don't have a leagueId, we can't show the modal. If max triggers per season is 0 or below, we shouldn't show the modal
  if (!leagueId || maxTriggersPerSeason <= 0) {
    console.debug(
      'shouldShowAutomaticPromptForPaidLeagueOptInModal: No leagueId or maxTriggersPerSeason is 0 or below'
    );
    return false;
  }
  const storageConstantsByFlow = getStorageConstantsByFlow(flow);
  const currentSavedLeagueId = Prefs.get(storageConstantsByFlow.currentLeagueId);
  // If we do not have a saved leagueId, or the current saved leagueId is not the same as the leagueId passed in, we know this is a new league for the user. Update the current leagueId, reset the prompt count and last prompt timestamp, and trigger the modal
  if (!currentSavedLeagueId || currentSavedLeagueId !== leagueId) {
    Prefs.set({[storageConstantsByFlow.currentLeagueId]: leagueId});
    Prefs.set({[storageConstantsByFlow.lastPromptTimestamp]: Date.now()});
    Prefs.set({[storageConstantsByFlow.promptCountSeason]: 1});
    return true;
  }
  const maxCooldownMinutes = ClientPropertyConstants.getLeaguesPaidLeaguePromptCooldownMinutes();
  const lastPromptTimestamp = Prefs.get(storageConstantsByFlow.lastPromptTimestamp) ?? Date.now();
  const msSinceLastPrompt = Date.now() - lastPromptTimestamp;
  const maxCooldownMs = maxCooldownMinutes * 60 * 1000;
  // If the user has not waited the full cooldown period, we don't show the modal and wait for the cooldown period to end
  if (msSinceLastPrompt < maxCooldownMs) {
    console.debug(
      'shouldShowAutomaticPromptForPaidLeagueOptInModal: User has not waited the full cooldown period'
    );
    return false;
  }
  const promptCountSeason = Prefs.get(storageConstantsByFlow.promptCountSeason) ?? 0;
  // If the prompt count for the season is less than the max, we should show the modal. Update time stamp and prompt count
  if (promptCountSeason < maxTriggersPerSeason) {
    Prefs.set({[storageConstantsByFlow.lastPromptTimestamp]: Date.now()});
    Prefs.set({[storageConstantsByFlow.promptCountSeason]: promptCountSeason + 1});
    return true;
  }
  // If the prompt count for the season is greater than the max, we don't show the modal. No need to update any storage data, as we'll always get here until the season ends.
  console.debug(
    'shouldShowAutomaticPromptForPaidLeagueOptInModal: User has reached the max prompt count for the season'
  );
  return false;
};

const getStorageConstantsByFlow = (flow: PaidLeagueOptInModalFlow) => {
  switch (flow) {
    case 'LAUNCH':
      return {
        currentLeagueId: PAID_LEAGUE_OPT_IN_MODAL_CURRENT_LEAGUE_ID_LAUNCH,
        lastPromptTimestamp: PAID_LEAGUE_OPT_IN_MODAL_LAST_PROMPT_TIMESTAMP_LAUNCH,
        promptCountSeason: PAID_LEAGUE_OPT_IN_MODAL_PROMPT_COUNT_SEASON_LAUNCH,
      };
    case 'LEAGUES_TAB':
      return {
        currentLeagueId: PAID_LEAGUE_OPT_IN_MODAL_CURRENT_LEAGUE_ID_LEAGUES_TAB,
        lastPromptTimestamp: PAID_LEAGUE_OPT_IN_MODAL_LAST_PROMPT_TIMESTAMP_LEAGUES_TAB,
        promptCountSeason: PAID_LEAGUE_OPT_IN_MODAL_PROMPT_COUNT_SEASON_LEAGUES_TAB,
      };
  }
};

//#endregion Functions for Automatic Prompt of Paid League Opt In Modal - END

//#region Global League

/**
 * Checks if a league is a global league
 * @param league
 * @returns boolean
 */
export const isGlobalLeague = (league: League | undefined | null) => {
  return !!league && !!league.global_leaderboard && league.global_leaderboard.length > 0;
};

/**
 * Gets the medalsBreakdown prop for <ProgressiveLeaguesV2PlayerMedalsModal/> from a leaguePlayer and a league. First list games with earned medals, then list games without medals, sorted alphabetically by game name.
 * @param leaguePlayer
 * @param league
 * @returns IMedalBreakdown[]
 */
export const getMedalsBreakdownForModal = (
  leaguePlayer: LeaguePlayer,
  league: League
): IMedalBreakdown[] => {
  const gamesInGlobalLeague = league.global_leaderboard;
  if (!gamesInGlobalLeague) {
    return [];
  }

  const medalsByGame = leaguePlayer.medals_by_game || [];

  // Create lookup object for globalLeagues
  const globalLeaguesGamesByGameId = gamesInGlobalLeague.reduce(
    (acc, game) => {
      acc[game.game_id] = game;
      return acc;
    },
    {} as Record<string, (typeof gamesInGlobalLeague)[0]>
  );

  // Create lookup object for medalsByGame
  const medalsByGameByGameId = medalsByGame.reduce(
    (acc, game) => {
      acc[game.game_id] = game;
      return acc;
    },
    {} as Record<string, (typeof medalsByGame)[0]>
  );

  const medalBreakdowns: IMedalBreakdown[] = [];

  // Phase 1: Add games with medals (already sorted by medal count from server)
  medalsByGame.forEach((gameFromPlayer) => {
    const game = globalLeaguesGamesByGameId[gameFromPlayer.game_id];

    if (!game) {
      return; // Skip this item (shouldn't happen, but protecting just in case)
    }

    medalBreakdowns.push({
      medalCountString: abbreviatedMedal(gameFromPlayer.medals),
      gameName: game.game_name,
      gameIcon: {uri: AssetHelper.pathForAssetNamed(game.game_icon_url, false)},
    });
  });

  // Phase 2: Add games without medals, sorted alphabetically by game name
  const gamesWithoutMedals = gamesInGlobalLeague
    .filter((game) => !medalsByGameByGameId[game.game_id])
    .sort((a, b) =>
      a.game_name.localeCompare(b.game_name, undefined, {numeric: true, sensitivity: 'base'})
    );

  gamesWithoutMedals.forEach((game) => {
    medalBreakdowns.push({
      medalCountString: abbreviatedMedal(0), // Show 0 medals for games without medals
      gameName: game.game_name,
      gameIcon: {uri: AssetHelper.pathForAssetNamed(game.game_icon_url, false)},
    });
  });

  return medalBreakdowns;
};

//#endregion

/**
 * Gets the most recently concluded progressive league from a list of leagues
 * @param concludedLeagues Array of concluded leagues
 * @returns The most recently concluded progressive league, or undefined if none exists
 */
export const getLastConcludedProgressiveLeague = (
  concludedLeagues: League[] | undefined
): League | undefined => {
  if (!concludedLeagues?.length) {
    return undefined;
  }

  // Filter out any leagues that the user is not participating
  const participatingLeagues = concludedLeagues.filter((league) => !!league.league_instance_id);

  const lastConcludedLeague = !!participatingLeagues.length
    ? participatingLeagues.reduce((a, b) => {
        // Find the league with the most recent end date
        return new Date(a.ends_at).getTime() > new Date(b.ends_at).getTime() ? a : b;
      })
    : undefined;

  if (!lastConcludedLeague) {
    return undefined;
  }

  // Return the league only if it's a progressive league
  return isProgressiveLeague(lastConcludedLeague) ? lastConcludedLeague : undefined;
};
